import { setTypingOnCanvas } from '../ui/keys';
import { applyOps, atLength, invert, applyToPoint, lengthNearest, nodeText, rulerIn, type Ruler, type Scene, type TextNode } from '@flobi/artwork-core';
import { firstStrongIsRtl, type Caret, type ParagraphText, type TextRect } from '@flobi/artwork-core/render';
import type * as Y from 'yjs';

/** What is being edited: a text node and the range the caret covers in it. */
export interface TextCursor {
  readonly id: string;
  /** Where the selection started, in UTF-16 units. */
  readonly anchor: number;
  /** Where it ends; the caret is drawn here. */
  readonly head: number;
}

const graphemes = typeof Intl !== 'undefined' && 'Segmenter' in Intl ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
const wordwise = typeof Intl !== 'undefined' && 'Segmenter' in Intl ? new Intl.Segmenter(undefined, { granularity: 'word' }) : null;

/**
 * The word a position falls in, as UTF-16 bounds.
 *
 * Asked of the platform's own segmenter, because "a word" is a question about language
 * rather than about spaces: Thai and Japanese are written without them, and a rule built
 * from spaces would select a whole sentence in either. The fallback — a run of the same
 * kind of character — is only ever reached where Intl.Segmenter is missing.
 */
function wordAround(text: string, at: number): [number, number] {
  if (text.length === 0) return [0, 0];
  const where = Math.min(Math.max(0, at), text.length - 1);
  if (wordwise) {
    const found = wordwise.segment(text).containing(where);
    if (found) return [found.index, found.index + found.segment.length];
  }
  const kind = (ch: string) => (/\s/.test(ch) ? 'space' : /[\p{L}\p{N}_]/u.test(ch) ? 'word' : 'other');
  const here = kind(text[where]!);
  let from = where;
  while (from > 0 && kind(text[from - 1]!) === here) from--;
  let to = where + 1;
  while (to < text.length && kind(text[to]!) === here) to++;
  return [from, to];
}

/**
 * Typing on the canvas.
 *
 * Characters go straight into the node's Y.Text, so two people in one text box merge
 * character by character rather than overwriting each other's paragraph, and undo takes
 * back typing the same way it takes back a drag.
 *
 * Keystrokes arrive through an off-screen textarea rather than raw key events: that is
 * what gives us input methods — Arabic keyboards, dictation, emoji, autocorrect — for
 * free, because the browser composes into a real field and tells us the result.
 */

/** The most characters a text box holds, under the document's limit for one run. */
const MAX_TEXT = 99_000;

export class TextEditing {
  private cursor: TextCursor | null = null;
  private field: HTMLTextAreaElement | null = null;
  private composing = false;
  /** Characters on their way to the clipboard, between ⌘C or ⌘X and the copy event. */
  private copying: string | null = null;
  private readonly unsubscribe: () => void;

  constructor(
    private readonly options: {
      page: Y.Doc;
      scene: Scene;
      text: ParagraphText;
      origin: unknown;
      /** Ask for a frame: the caret moved or the text changed. */
      onChange: () => void;
      /**
       * Editing ended. `left` is true only when the person pressed Esc to leave the text,
       * which is the one ending that hands the canvas back to Select. Ending because
       * another tool took over used to do it too, so the Pen's first press after typing
       * switched to Select and lost its point (QA 2026-09-24, text-F19).
       */
      onExit: (left: boolean) => void;
      /**
       * ⌘Z / ⇧⌘Z pressed in the field the keys arrive through: the document's own undo.
       * It used to go nowhere — the canvas leaves a textarea's keys alone, so undo did
       * nothing while typing until Esc (QA 2026-09-24, text-F12).
       */
      onUndo?: (redo: boolean) => void;
      /** Tells the person, briefly, that something they typed or pasted was cut short. */
      onNotice?: (message: string) => void;
    },
  ) {
    /**
     * The text being typed in can go out from under the caret: undone, deleted from
     * Layers, deleted by someone else. Nothing used to notice, so a caret blinked on for a
     * text that no longer existed, and redo — which brings the text back under the same
     * id — brought it back with a caret in it that the keyboard no longer reached
     * (QA 2026-09-24, text-F25). Typing ends the moment its text is gone.
     *
     * And when someone else shortens it, the caret is kept inside what is left, so the
     * next keystroke is not aimed past the end of the text.
     */
    this.unsubscribe = options.scene.subscribe((change) => {
      const cursor = this.cursor;
      if (!cursor) return;
      if (change.removed.has(cursor.id) || !this.node(cursor.id)) {
        this.stop();
        return;
      }
      if (!change.updated.has(cursor.id)) return;
      const length = textOf(this.node(cursor.id)!).length;
      if (cursor.anchor > length || cursor.head > length) {
        this.cursor = { id: cursor.id, anchor: Math.min(cursor.anchor, length), head: Math.min(cursor.head, length) };
        this.options.onChange();
      }
    });
  }

  get current(): TextCursor | null {
    return this.cursor;
  }

  get editingId(): string | null {
    return this.cursor?.id ?? null;
  }

  /** Starts editing a node, with the caret at an index or covering everything. */
  start(id: string, at: number | 'all' = 0): void {
    const node = this.node(id);
    if (!node) return;
    // A locked or hidden text is not opened for typing. Layers can choose one and Return
    // opened it, and typing went straight through the lock — the Y.Text is written
    // directly, past applyOps' lock check (QA 2026-09-24, text-F22).
    if (this.options.scene.lockedHere(id) || this.options.scene.hiddenHere(id)) return;
    // Moving straight from one text to another leaves the first behind as it would be
    // left by Esc: gone, if it was emptied (text-F16).
    const previous = this.cursor?.id;
    if (previous && previous !== id) this.dropIfEmpty(previous);
    const length = textOf(node).length;
    this.cursor =
      at === 'all' ? { id, anchor: 0, head: length } : { id, anchor: clamp(at, length), head: clamp(at, length) };
    // A box can arrive with text already in it — a placeholder — and nothing has measured
    // it yet, so its stored size is zero and everything that reads bounds without fonts
    // thinks it is a point. Measuring on the way in costs one layout of text that is about
    // to be laid out anyway.
    this.resize(id);
    this.focusField();
    setTypingOnCanvas(true);
    this.options.onChange();
  }

  /** Starts editing where the pointer landed, in world coordinates. */
  startAt(id: string, world: { x: number; y: number }): void {
    const index = this.indexAtWorld(id, world);
    this.start(id, index ?? 0);
  }

  /** Puts the keyboard back in the text being typed. */
  refocus(): void {
    if (this.cursor) this.focusField();
  }

  /** Ends typing. `left` says the person pressed Esc to leave the text (text-F19). */
  stop(left = false): void {
    if (!this.cursor) return;
    const { id } = this.cursor;
    this.cursor = null;
    setTypingOnCanvas(false);
    this.blurField();
    // Before onExit, which closes the typing step: the text going is part of that step,
    // so one undo brings back the text with its letters.
    this.dropIfEmpty(id);
    this.options.onChange();
    this.options.onExit(left);
  }

  /**
   * An emptied text is removed rather than left behind (QA 2026-09-24, text-F16).
   *
   * It stayed as an invisible layer with a zero-width box nobody could click, found only
   * in Layers. A lock refuses the delete, which is right: nothing was typed into it.
   */
  private dropIfEmpty(id: string): void {
    const node = this.node(id);
    if (!node || textOf(node).length > 0) return;
    applyOps(this.options.page, [{ op: 'delete', id }], { scene: this.options.scene, origin: this.options.origin });
  }

  /**
   * A second click takes the word under it; a third takes the whole box.
   *
   * What every other text field on the machine does, and the reason it has to be said
   * here is that a canvas has no text field to inherit it from — the box people type
   * into is a drawing, so every habit it should have is one this has to grow.
   */
  selectWordAt(world: { x: number; y: number }): void {
    const cursor = this.cursor;
    const node = cursor && this.node(cursor.id);
    if (!cursor || !node) return;
    const index = this.indexAtWorld(cursor.id, world);
    if (index === null) return;
    const [from, to] = wordAround(textOf(node), index);
    this.cursor = { id: cursor.id, anchor: from, head: to };
    this.focusField();
    this.options.onChange();
  }

  selectAll(): void {
    const cursor = this.cursor;
    const node = cursor && this.node(cursor.id);
    if (!cursor || !node) return;
    this.cursor = { id: cursor.id, anchor: 0, head: textOf(node).length };
    this.focusField();
    this.options.onChange();
  }

  /** Moves the caret to a point, extending the selection while dragging. */
  pointTo(world: { x: number; y: number }, extend: boolean): void {
    const cursor = this.cursor;
    if (!cursor) return;
    // Using a panel takes the keyboard away from the field; touching the canvas again
    // has to bring it back, or the caret would sit there swallowing every keystroke.
    this.focusField();
    const index = this.indexAtWorld(cursor.id, world);
    if (index === null) return;
    this.cursor = { id: cursor.id, anchor: extend ? cursor.anchor : index, head: index };
    this.options.onChange();
  }

  /**
   * The curve this box rides, in the box's own coordinates, or null for ordinary text.
   *
   * Read from the path node every time rather than remembered: the path is somebody
   * else's object and can move under us, and the words are supposed to follow it.
   */
  private along(id: string, node: TextNode): Ruler | null {
    const bound = node.onPath;
    if (!bound) return null;
    const path = this.options.scene.get(bound.id);
    if (path?.type !== 'path') return null;
    const sub = path.path?.subpaths?.[0];
    if (!sub || sub.points.length < 2) return null;
    return rulerIn(sub.points, sub.closed, this.options.scene.worldTransform(bound.id), this.options.scene.worldTransform(id));
  }

  /** True when the point is inside the box being edited, or near the curve it rides. */
  hits(world: { x: number; y: number }): boolean {
    const cursor = this.cursor;
    if (!cursor) return false;
    const local = this.toLocal(cursor.id, world);
    const node = this.node(cursor.id);
    if (!local || !node) return false;
    const ruler = this.along(cursor.id, node);
    if (ruler) {
      // Text on a curve has no box worth testing; what it has is a line, and a band
      // either side of it as tall as the letters standing on it.
      const near = atLength(ruler, lengthNearest(ruler, local));
      if (!near) return false;
      const reach = (node.style?.fontSize ?? 16) * 1.2;
      return Math.hypot(local.x - near.x, local.y - near.y) <= reach;
    }
    const { width, height } = this.size(node);
    return local.x >= -2 && local.y >= -2 && local.x <= width + 2 && local.y <= height + 2;
  }

  /**
   * The caret, as the two ends of the stroke that draws it, in the node's coordinates.
   *
   * Two points rather than a place and a height: on a curve the caret leans, standing
   * square to the path rather than square to the page.
   */
  caret(): { id: string; from: { x: number; y: number }; to: { x: number; y: number } } | null {
    const cursor = this.cursor;
    const node = cursor && this.node(cursor.id);
    if (!cursor || !node) return null;
    return { id: cursor.id, ...this.options.text.caretLine(node, cursor.head, this.along(cursor.id, node)) };
  }

  /**
   * The boxes behind selected text, in the node's own coordinates.
   *
   * Empty for text on a curve: the band would have to be a run of turned quadrilaterals
   * following the path, and a row of upright rectangles laid over curved text is worse
   * than none — it covers the letters it is meant to be behind.
   */
  selectionRects(): TextRect[] {
    const cursor = this.cursor;
    const node = cursor && this.node(cursor.id);
    if (!cursor || !node || cursor.anchor === cursor.head) return [];
    if (node.onPath) return [];
    return this.options.text.rectsForRange(node, cursor.anchor, cursor.head);
  }

  /**
   * Handles a key while editing. Returns true when it was ours, so the canvas doesn't
   * also treat it as a shortcut — Delete must not delete the node while typing.
   */
  handleKey(event: KeyboardEvent): boolean {
    const cursor = this.cursor;
    const node = cursor && this.node(cursor.id);
    if (!cursor || !node) return false;
    const text = textOf(node);
    const meta = event.metaKey || event.ctrlKey;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.stop(true);
      return true;
    }
    if (meta && event.key.toLowerCase() === 'a') {
      event.preventDefault();
      this.cursor = { id: cursor.id, anchor: 0, head: text.length };
      this.options.onChange();
      return true;
    }
    // Undo stays with the document's own history, not the field's. Pressed in the field
    // the keys arrive through, it is answered here (text-F12); pressed anywhere else it
    // goes on to the canvas, or to the panel field that has it.
    if (meta && !event.altKey && event.key.toLowerCase() === 'z') {
      if (event.target !== this.field || !this.options.onUndo) return false;
      event.preventDefault();
      this.options.onUndo(event.shiftKey);
      return true;
    }
    if (meta && !event.shiftKey && !event.altKey && (event.key.toLowerCase() === 'c' || event.key.toLowerCase() === 'x')) {
      return this.copy(event, text, event.key.toLowerCase() === 'x');
    }

    /**
     * The Mac's reaches (QA 2026-09-24, text-F13): a letter plain, a word with Option, to
     * the line's end with ⌘ — for deleting and for moving alike. Every one of them used to
     * go one letter.
     */
    const back = (at: number): number =>
      event.metaKey ? this.options.text.lineRange(node, at).start : event.altKey ? wordBack(text, at) : stepBack(text, at);
    const ahead = (at: number): number =>
      event.metaKey ? this.options.text.lineRange(node, at).end : event.altKey ? wordForward(text, at) : stepForward(text, at);

    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      if (cursor.anchor !== cursor.head) this.replaceSelection('');
      else if (event.key === 'Backspace' && cursor.head > 0) this.deleteRange(Math.min(back(cursor.head), stepBack(text, cursor.head)), cursor.head);
      else if (event.key === 'Delete' && cursor.head < text.length) this.deleteRange(cursor.head, Math.max(ahead(cursor.head), stepForward(text, cursor.head)));
      return true;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.insert('\n');
      return true;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const forward = this.isRtl(node) ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
      const next = forward ? ahead(cursor.head) : back(cursor.head);
      this.moveTo(next, event.shiftKey);
      return true;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      // ⌘↑ and ⌘↓ go to the start and the end of the whole text, as in every Mac field.
      if (event.metaKey) {
        this.moveTo(event.key === 'ArrowUp' ? 0 : text.length, event.shiftKey);
        return true;
      }
      const caret = this.options.text.caretAt(node, cursor.head);
      const y = event.key === 'ArrowUp' ? caret.y - caret.height / 2 : caret.y + caret.height * 1.5;
      this.moveTo(this.options.text.indexAt(node, caret.x, y), event.shiftKey);
      return true;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const line = this.options.text.lineRange(node, cursor.head);
      this.moveTo(event.key === 'Home' ? line.start : line.end, event.shiftKey);
      return true;
    }
    return false;
  }

  /** Puts text in at the caret, replacing whatever is selected. */
  insert(value: string): void {
    if (!this.cursor || !value) return;
    // A text box holds at most MAX_TEXT characters. Past the document's limit every edit
    // to the box failed and the server's repair then wiped the text for everyone, where
    // undo could not reach it (QA 2026-09-24, text-F1). So a paste is cut to fit, and
    // the person is told.
    const node = this.node(this.cursor.id);
    const selected = Math.abs(this.cursor.head - this.cursor.anchor);
    const room = MAX_TEXT - ((node ? textOf(node).length : 0) - selected);
    if (room <= 0) {
      this.options.onNotice?.('This text box is full.');
      return;
    }
    if (value.length > room) {
      value = value.slice(0, room);
      this.options.onNotice?.(`Only the first ${room.toLocaleString()} characters fit in this text box.`);
    }
    this.replaceSelection(value);
  }

  /**
   * ⌘C and ⌘X on selected characters (QA 2026-09-24, text-F3).
   *
   * They used to do nothing at all: the canvas's own copy and cut are for layers and sit
   * behind its check for a focused field, and the field the keys arrive through is always
   * empty, so the browser's copy found nothing to copy. The only way to move words was
   * to type them again.
   *
   * The selected characters are put in the field and selected there, and the key is left
   * to do what it does everywhere — the browser copies a selection in a focused field
   * without asking anyone's permission, which the async clipboard API cannot promise. The
   * copy event it fires is answered with exactly the characters as plain text; should no
   * event come (a browser that fires none), the clipboard API is tried a moment later.
   * Cut then takes the characters out of the text, as one step undo can put back.
   *
   * Pressed in some other field — a panel's search box — the keys are that field's.
   */
  private copy(event: KeyboardEvent, text: string, cut: boolean): boolean {
    const cursor = this.cursor;
    if (!cursor) return false;
    const target = event.target as { tagName?: string; isContentEditable?: boolean } | null;
    const elsewhere =
      target !== this.field &&
      !!target &&
      (target.isContentEditable === true || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT');
    if (elsewhere) return false;
    const [from, to] = cursor.anchor <= cursor.head ? [cursor.anchor, cursor.head] : [cursor.head, cursor.anchor];
    // Nothing selected: nothing to copy — and not the whole layer either, which is what
    // the canvas would copy if the key went on to it.
    if (to <= from) {
      event.preventDefault();
      return true;
    }
    const selected = text.slice(from, to);
    this.focusField();
    const field = this.field;
    if (field) {
      field.value = selected;
      field.select();
    }
    this.copying = selected;
    window.setTimeout(() => {
      if (this.copying === null) return;
      const late = this.copying;
      this.copying = null;
      if (this.field?.value === late) this.field.value = '';
      void navigator.clipboard?.writeText(late).catch(() => undefined);
    }, 0);
    if (cut) this.replaceSelection('');
    return true;
  }

  /** The browser's copy or cut, from the field: the characters ⌘C or ⌘X chose, as plain text. */
  private answerClipboard(event: ClipboardEvent, field: HTMLTextAreaElement): void {
    const selected = this.copying;
    if (selected === null) return;
    this.copying = null;
    event.preventDefault();
    event.clipboardData?.setData('text/plain', selected);
    field.value = '';
  }

  /**
   * ⌘V while typing: words only (QA 2026-09-24, text-F15).
   *
   * The browser pasted the clipboard's plain text into the field, and a shape copied on
   * the canvas travels as plain text too — its whole record, as JSON — so the text got a
   * block of code. Plain text goes in, with Windows line ends made single; a copied shape
   * or a picture puts nothing in a text being typed.
   */
  private paste(event: ClipboardEvent): void {
    event.preventDefault();
    const plain = event.clipboardData?.getData('text/plain') ?? '';
    if (!plain || isCopiedArtwork(plain)) return;
    this.insert(plain.replace(/\r\n?/g, '\n'));
  }

  dispose(): void {
    this.unsubscribe();
    this.cursor = null;
    this.blurField();
  }

  // ── the document ─────────────────────────────────────────────────────

  /**
   * Whether the text may be written. The Y.Text is written directly, which skips the
   * lock check every op goes through — so a text locked while it was open took typing
   * straight through the lock, and then refused its own resize (text-F22).
   */
  private writable(id: string): boolean {
    if (!this.options.scene.lockedHere(id)) return true;
    this.options.onNotice?.('This text is locked.');
    return false;
  }

  private replaceSelection(value: string): void {
    const cursor = this.cursor;
    if (!cursor || !this.writable(cursor.id)) return;
    const [from, to] = cursor.anchor <= cursor.head ? [cursor.anchor, cursor.head] : [cursor.head, cursor.anchor];
    const text = nodeText(this.options.page, cursor.id);
    if (!text) return;

    this.options.page.transact(() => {
      if (to > from) text.delete(from, to - from);
      if (value) text.insert(from, value);
    }, this.options.origin);

    const head = from + value.length;
    this.cursor = { id: cursor.id, anchor: head, head };
    this.resize(cursor.id);
    this.options.onChange();
  }

  private deleteRange(from: number, to: number): void {
    const cursor = this.cursor;
    if (!cursor || to <= from || !this.writable(cursor.id)) return;
    const text = nodeText(this.options.page, cursor.id);
    if (!text) return;
    this.options.page.transact(() => text.delete(from, to - from), this.options.origin);
    this.cursor = { id: cursor.id, anchor: from, head: from };
    this.resize(cursor.id);
    this.options.onChange();
  }

  /**
   * A box that hugs its text carries its measured size in the document, so everything
   * that doesn't have fonts — bounds, culling, selection, the backend — still knows how
   * big the text is.
   */
  private resize(id: string): void {
    this.refit(id);
  }

  /**
   * Measures a text that hugs its content and writes its box back. Public because a text
   * changes size from outside the editor too — the quick bar's Size, the inspector's
   * Weight, a new font — and its box used to stay where typing last left it, so the text
   * drew cut off and the part outside could not be clicked (QA 2026-09-24, text-F6).
   */
  refit(id: string, origin: unknown = this.options.origin): void {
    const node = this.node(id);
    if (!node || node.sizing === 'fixed') return;
    const measured = this.options.text.measure(node);
    const width = node.sizing === 'auto-width' ? Math.ceil(measured.longestLine) : node.width;
    const height = Math.ceil(measured.height);
    if (Math.abs(width - node.width) < 0.5 && Math.abs(height - node.height) < 0.5) return;
    applyOps(this.options.page, [{ op: 'set', id, props: { width, height } }], {
      scene: this.options.scene,
      origin,
    });
  }

  private moveTo(index: number, extend: boolean): void {
    const cursor = this.cursor;
    const node = cursor && this.node(cursor.id);
    if (!cursor || !node) return;
    const head = clamp(index, textOf(node).length);
    this.cursor = { id: cursor.id, anchor: extend ? cursor.anchor : head, head };
    this.options.onChange();
  }

  private node(id: string): TextNode | null {
    const node = this.options.scene.get(id);
    return node && node.type === 'text' ? (node as TextNode) : null;
  }

  private size(node: TextNode): { width: number; height: number } {
    if (node.width > 0 && node.height > 0) return { width: node.width, height: node.height };
    const measured = this.options.text.measure(node);
    return { width: Math.max(node.width, measured.longestLine), height: Math.max(node.height, measured.height) };
  }

  private isRtl(node: TextNode): boolean {
    if (node.direction === 'rtl') return true;
    if (node.direction === 'ltr') return false;
    // Mixed paragraphs move logically for now; visual movement through a bidi run
    // needs the run map, which is M1 work. The direction itself is the shaper's own
    // first-strong-character rule: "any Hebrew or Arabic anywhere" swapped the arrows in
    // an English sentence holding one Arabic word (QA 2026-09-24, text-F18).
    return firstStrongIsRtl(textOf(node));
  }

  private toLocal(id: string, world: { x: number; y: number }): { x: number; y: number } | null {
    const inverse = invert(this.options.scene.worldTransform(id));
    return inverse ? applyToPoint(inverse, world.x, world.y) : null;
  }

  private indexAtWorld(id: string, world: { x: number; y: number }): number | null {
    const node = this.node(id);
    const local = this.toLocal(id, world);
    if (!node || !local) return null;
    const ruler = this.along(id, node);
    // How far along the curve the press landed is the flat x it would have had, so the
    // same lookup answers for curved text as for straight.
    if (ruler) return this.options.text.indexAlong(node, ruler, local);
    return this.options.text.indexAt(node, local.x, local.y);
  }

  // ── the off-screen field ─────────────────────────────────────────────

  /**
   * Puts the keyboard in the field, and keeps it there when the press that asked takes it.
   *
   * A browser moves focus as the DEFAULT action of a press, which runs AFTER the handlers
   * have. So a tool that starts editing on pointerdown focuses the field and then the very
   * same press pulls the keyboard back out to the body: a caret blinking over a box that
   * swallows every key, which is indistinguishable from a dead keyboard. Asking again on
   * the next turn of the loop, once the press is over, is what makes starting to edit from
   * a press work at all — area type only ever worked because it starts on the release, by
   * which time the theft has already happened.
   *
   * It asks again only when nothing meaningful has taken the keyboard since, so a panel
   * someone opened in the meantime keeps it.
   */
  private focusField(): void {
    if (!this.field) {
      const field = document.createElement('textarea');
      field.setAttribute('aria-label', 'Text');
      field.autocapitalize = 'off';
      field.autocomplete = 'off';
      field.spellcheck = false;
      // Off screen rather than hidden: a field with no box gets no input method.
      field.style.cssText = 'position:fixed;top:0;left:-9999px;width:1px;height:1px;opacity:0;padding:0;border:0;resize:none;font-size:16px;';
      field.addEventListener('compositionstart', () => {
        this.composing = true;
      });
      field.addEventListener('compositionend', () => {
        this.composing = false;
        this.take(field);
      });
      field.addEventListener('input', () => {
        // While an input method is composing, the field holds a draft; take it only
        // once the person has settled on it.
        if (!this.composing) this.take(field);
      });
      field.addEventListener('copy', (event) => this.answerClipboard(event, field));
      field.addEventListener('cut', (event) => this.answerClipboard(event, field));
      field.addEventListener('paste', (event) => this.paste(event));
      document.body.append(field);
      this.field = field;
    }
    const field = this.field;
    field.value = '';
    field.focus({ preventScroll: true });
    window.setTimeout(() => {
      const active = document.activeElement;
      if (!this.cursor || this.field !== field || active === field) return;
      // Only from the two that mean "nobody is typing anywhere": the page itself, and the
      // canvas the press landed on.
      if (active && active !== document.body && active.tagName !== 'CANVAS') return;
      field.focus({ preventScroll: true });
    }, 0);
  }

  private take(field: HTMLTextAreaElement): void {
    const value = field.value;
    field.value = '';
    if (value) this.insert(value);
  }

  private blurField(): void {
    this.field?.remove();
    this.field = null;
    this.composing = false;
  }
}

/** Whether clipboard text is layers copied on the canvas (`{ artwork: [...] }`), not words. */
function isCopiedArtwork(text: string): boolean {
  if (!text.startsWith('{')) return false;
  try {
    const read = JSON.parse(text) as { artwork?: unknown } | null;
    return !!read && typeof read === 'object' && Array.isArray(read.artwork);
  } catch {
    return false;
  }
}

/** The characters of a text node. */
export function textOf(node: TextNode): string {
  return (node.runs ?? []).map((run) => run.text).join('');
}

const clamp = (index: number, length: number) => Math.max(0, Math.min(length, Math.round(index)));

/** One grapheme back, so a flag or an emoji with a skin tone deletes in one press. */
function stepBack(text: string, index: number): number {
  if (index <= 0) return 0;
  if (!graphemes) return index - 1;
  let last = 0;
  for (const { index: start } of graphemes.segment(text.slice(0, index))) last = start;
  return last;
}

function stepForward(text: string, index: number): number {
  if (index >= text.length) return text.length;
  if (!graphemes) return index + 1;
  for (const { index: start, segment } of graphemes.segment(text)) {
    if (start >= index) return start + segment.length;
  }
  return text.length;
}

/** The words of a text, as UTF-16 bounds — the platform's idea of a word, as in `wordAround`. */
function wordsOf(text: string): [number, number][] {
  if (wordwise) {
    const out: [number, number][] = [];
    for (const one of wordwise.segment(text)) if (one.isWordLike) out.push([one.index, one.index + one.segment.length]);
    return out;
  }
  return [...text.matchAll(/[\p{L}\p{N}_]+/gu)].map((match) => [match.index!, match.index! + match[0].length]);
}

/** Option-Left: the start of the word the caret is in, or of the one before it. */
function wordBack(text: string, index: number): number {
  let found = 0;
  for (const [start] of wordsOf(text)) {
    if (start >= index) break;
    found = start;
  }
  return found;
}

/** Option-Right: the end of the word the caret is in, or of the one after it. */
function wordForward(text: string, index: number): number {
  for (const [, end] of wordsOf(text)) if (end > index) return end;
  return text.length;
}
